import jsPDF from "jspdf";
import "jspdf-autotable";
import { toast } from "react-toastify";
import { toPng } from "html-to-image";
import {
  TrendCurveChartData,
  TrendItem,
} from "../../interface/DesignerInterface";
import {
  formatTrendCurvePeriodLabel,
  sortTrendCurvePeriodsForDisplay,
} from "./trendCurveDataUtils";
import { createTrendCurveSvgMarkup } from "./trendCurveChartRenderer";
import { TREND_CURVE_EXPANDED_CHART_WIDTH } from "../../constants/trendCurveConstants";
import { formatTrendHeaderChips } from "../designerPage-utils";
import { svgToBase64DataUrl } from "./svgDownloadUtils";
import {
  drawPdfHeader,
  drawPdfSectionTitle,
  drawPdfTitle,
  elegantTablePageDecorator,
  loadPoppinsFonts,
  PDF_TREND_CURVE_TABLE_STYLES,
} from "./pdfEleganceUtils";

export const handleTrendCurveDownload = async (
  setDownloadLoading: React.Dispatch<React.SetStateAction<boolean>>,
  trendCurveData: TrendCurveChartData,
  trendsCurveRawData: TrendItem[],
  svgContainer?: HTMLElement,
  filterType?: string,
  curveWindowType?: string
) => {
  setDownloadLoading(true);
  try {
    if (
      !trendCurveData ||
      !trendCurveData.datasets ||
      trendCurveData.datasets.length === 0
    ) {
      toast.error("No graph available to download");
      return;
    }

    const captureContainer = async (): Promise<{
      imgData: string;
      width: number;
      height: number;
    } | null> => {
      if (!svgContainer) return null;
      try {
        const imgData = await toPng(svgContainer, {
          pixelRatio: 4,
          cacheBust: true,
          backgroundColor: "#ffffff",
        });
        const img = new Image();
        await new Promise<void>((resolve, reject) => {
          img.onload = () => resolve();
          img.onerror = () =>
            reject(new Error("Failed to load captured image"));
          img.src = imgData;
        });
        return { imgData, width: img.naturalWidth, height: img.naturalHeight };
      } catch {
        return null;
      }
    };

    const captureSvgFallback = (): Promise<{
      imgData: string;
      width: number;
      height: number;
    } | null> =>
      new Promise(resolve => {
        const svgMarkup = createTrendCurveSvgMarkup(
          trendCurveData,
          TREND_CURVE_EXPANDED_CHART_WIDTH,
          { expanded: true }
        );
        if (!svgMarkup) {
          resolve(null);
          return;
        }
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement("canvas");
          const context = canvas.getContext("2d");
          const scaleFactor = 4;
          canvas.width = img.width * scaleFactor;
          canvas.height = img.height * scaleFactor;
          context?.scale(scaleFactor, scaleFactor);
          context?.drawImage(img, 0, 0);
          resolve({
            imgData: canvas.toDataURL("image/png"),
            width: canvas.width,
            height: canvas.height,
          });
        };
        img.onerror = () => resolve(null);
        img.src = svgToBase64DataUrl(svgMarkup);
      });

    const captured = svgContainer
      ? await captureContainer()
      : await captureSvgFallback();

    if (!captured) {
      toast.error("No graph available to download");
      return;
    }

    const { imgData, width: imgWidth, height: capturedImgHeight } = captured;

    const pdf = new jsPDF({
      orientation: "landscape",
      unit: "pt",
      format: "a3",
    });

    await loadPoppinsFonts(pdf);

    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();

    const tableWidth = pageWidth * 0.8;
    const tableMargin = (pageWidth - tableWidth) / 2;

    const chartWidth = tableWidth;
    const imgHeight = (capturedImgHeight / imgWidth) * chartWidth;
    const xOffset = tableMargin;

    drawPdfHeader(pdf, pageWidth);
    drawPdfTitle(pdf, "Trend Curve Graph", pageWidth, 70, false);

    // Add filter and curve window info below title
    let infoY = 92;
    if (filterType) {
      pdf.setFontSize(10);
      pdf.setFont("Poppins", "normal");
      pdf.setTextColor("#666666");
      pdf.text(`Filter: ${filterType}`, xOffset, infoY);
      infoY += 14;
    }
    if (curveWindowType) {
      pdf.setFontSize(10);
      pdf.setFont("Poppins", "normal");
      pdf.setTextColor("#666666");
      pdf.text(`Curve Window: ${curveWindowType}`, xOffset, infoY);
      infoY += 14;
    }

    pdf.addImage(imgData, "PNG", xOffset, infoY + 8, chartWidth, imgHeight);

    const tableStartY = infoY + 8 + imgHeight + 30;
    drawPdfSectionTitle(pdf, "Trend Curve Table", pageWidth, tableStartY);

    const sortedPeriods = trendsCurveRawData[0]?.data
      ? sortTrendCurvePeriodsForDisplay(trendsCurveRawData[0].data)
      : [];

    const tableHead = [
      [
        "Trend No.",
        "Trend",
        ...sortedPeriods.map(item => formatTrendCurvePeriodLabel(item.period)),
      ],
    ];
    const tableData = [...trendsCurveRawData]
      .sort((a, b) => a.tNo - b.tNo)
      .map(row => [
        String(row.tNo),
        formatTrendHeaderChips(row.Trend),
        ...sortedPeriods.map(item => {
          const point = row.data.find(p => p.period === item.period);
          return point ? String(point.contribution) : "";
        }),
      ]);

    // @ts-expect-error: autoTable is not recognized by TypeScript
    pdf.autoTable({
      head: tableHead,
      body: tableData || [],
      startY: tableStartY + 30,
      ...PDF_TREND_CURVE_TABLE_STYLES,
      tableWidth: tableWidth,
      margin: {
        left: tableMargin,
        right: tableMargin,
        bottom: 60,
      },
      didDrawPage: elegantTablePageDecorator(pageWidth, pageHeight),
    });

    pdf.save("Trend Curve.pdf");
  } catch (error) {
    console.error("Error generating Trend Curve report", error);
    toast.error("Error generating report");
  } finally {
    setDownloadLoading(false);
  }
};
